import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { useLang } from "@/i18n";

const EASE = [0.22, 1, 0.36, 1];
const HERO_BG =
  "https://images.unsplash.com/photo-1623092350739-4635ce84d47c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxib2R5YnVpbGRlciUyMGNpbmVtYXRpYyUyMGd5bXxlbnwwfHx8fDE3ODgxOTAyNDl8MA&ixlib=rb-4.1.0&q=85";

const Hero = () => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      ref={ref}
      className="relative min-h-screen flex flex-col justify-end overflow-hidden"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={HERO_BG}
          alt="FitBersama gym"
          className="w-full h-full object-cover scale-110"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-x-0 bottom-0 h-64 bg-[#050505]" style={{ maskImage: "linear-gradient(to top, black, transparent)", WebkitMaskImage: "linear-gradient(to top, black, transparent)" }} />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 container-x pt-40 pb-16">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
          className="overline-gold mb-6"
        >
          {t.hero.overline}
        </motion.p>

        <h1 className="font-display uppercase leading-[0.82] tracking-tighter">
          <span className="block overflow-hidden">
            <motion.span
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1.1, delay: 0.25, ease: EASE }}
              className="block text-[24vw] md:text-[15vw] lg:text-[13vw]"
            >
              FIT<span className="text-[#D91624]">BERSAMA</span>
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span
              key={lang}
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1.1, delay: 0.1, ease: EASE }}
              className="block text-[9vw] md:text-[5.5vw] lg:text-[4.5vw] text-neutral-300"
            >
              {t.hero.subtitle}
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
          className="mt-8 max-w-xl text-base md:text-lg text-neutral-400 leading-relaxed"
        >
          {t.hero.paragraph}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <button
            data-testid="hero-start-journey-btn"
            onClick={() => scrollToId("coaching")}
            className="btn-primary"
          >
            {t.hero.ctaPrimary} <ArrowRight size={16} />
          </button>
          <button
            data-testid="hero-free-guide-btn"
            onClick={() => scrollToId("free-guide")}
            className="btn-outline"
          >
            {t.hero.ctaSecondary}
          </button>
        </motion.div>

        <motion.div
          data-testid="hero-stats-bar"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.1, ease: EASE }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 border-t border-white/10"
        >
          {t.hero.stats.map((s) => (
            <div key={s.label} className="py-6 pr-6 border-r border-white/10 last:border-r-0 md:[&:nth-child(2)]:border-r">
              <p className="font-display text-3xl md:text-5xl text-white">{s.value}</p>
              <p className="mt-1 text-xs uppercase tracking-[0.2em] text-neutral-500 font-bold">
                {s.label}
              </p>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-down"
        onClick={() => scrollToId("story")}
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
        className="absolute bottom-6 right-6 md:right-12 z-10 text-neutral-500 hover:text-[#D4AF37] transition-colors duration-300 cursor-pointer"
        aria-label={t.hero.scrollLabel}
      >
        <ArrowDown size={22} />
      </motion.button>
    </section>
  );
};

export default Hero;
